import { Location } from 'react-router';
import { createSearchParams } from 'react-router-dom';

import { SitecoreLink } from '@/default-config';

type Path = {
    path: string;
    link: SitecoreLink;
};

type PathWithSearch = {
    path: string;
    link: SitecoreLink;
    searchParam: readonly string[];
};

export const paths = {
    HOME_PAGE: { path: '/', link: 'linkCardOverview' } as Path,
    MY_CARD_PAGE: { path: '/', link: 'linkCardOverview' } as Path,
    CARD_PAGE: {
        path: '/kort/detaljer',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkCardDetails',
    } as PathWithSearch,
    CARD_TRANSACTION_PAGE: {
        path: '/kort/transaksjoner',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkTransactions',
    } as PathWithSearch,
    COMPLAINT_PAGE: { path: '/kort/reklamasjon', link: 'linkComplaint' } as Path,
    SPENDING_LIMITS_PAGE: { path: '/kort/brukstillatelser', link: 'linkSpendingLimits' } as Path,
    TRANSFER_PAGE: {
        path: '/kort/transfer',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkTransfer',
    } as PathWithSearch,
    CARD_DOCUMENTS_PAGE: {
        path: '/kort/detaljer/dokumenter',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkDocuments',
    } as PathWithSearch,
    CARD_CONTROL_PAGE: {
        path: '/kort/brukstillatelser',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkCardControl',
    } as PathWithSearch,
    CARD_RULES_PAGE: {
        path: '/kort/regionsperre',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkCardRules',
    } as PathWithSearch,
    CURRENCY_CALCULATOR_PAGE: { path: '/valutakalkulator', link: 'linkVisaExchangeCalculator' } as Path,
    INSURANCE_PROOF_PAGE: { path: '/kort/forsikringsbevis', link: 'linkInsuranceProof' } as Path,
    SELECT_BLOCK_TYPE_PAGE: {
        path: '/kort/sperr',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkBlockSelectedCard',
    } as PathWithSearch,
    SELECT_BLOCK_REORDER_PAGE: {
        path: '/kort/detaljer/bestill-sperr',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkBlockSelectedCard',
    } as PathWithSearch,
    SOFT_BLOCK_CARD_CONFIRM_PAGE: {
        path: '/kort/sperr/midlertidig/oppsummering',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkSoftBlockCardConfirm',
    } as PathWithSearch,
    SELECT_REASON_PAGE: {
        path: '/kort/sperr/permanent',
        searchParam: ['cid', 'ctype', 'cardDetailsRedirect'] as const,
        link: 'linkHardBlockCardSelectReason',
    } as PathWithSearch,
    SELECT_REORDER_CARD_PAGE: {
        path: '/kort/sperr/permanent/rebestill',
        link: 'linkSelectOrderNewCard',
        searchParam: ['cid', 'ctype', 'reason'],
    } as PathWithSearch,
    PIN_CHANGE_DECISION_PAGE: {
        path: '/kort/sperr/permanent/rebestill/behold-pin',
        link: 'linkHardBlockCardPinChangeDecision',
    } as Path,
    NEW_PIN_ENTRY_PAGE: { path: '/kort/sperr/permanent/rebestill/velg-ny-pin' } as Path,
    HARD_BLOCK_CARD_CONFIRM_PAGE: {
        path: '/kort/sperr/permanent/oppsummering',
        link: 'linkHardBlockCardConfirm',
        searchParam: [],
    } as PathWithSearch,
    HARD_BLOCK_CARD_CONFIRM_SUMMARY_PAGE: {
        path: '/kort/sperr/permanent/rebestill/oppsummering',
        link: 'linkHardBlockCardConfirm',
    } as Path,
    ORDER_CARD_PAGE: { path: '/kort/bestill', link: 'linkOrderCardSelectAccount' } as Path,
    ORDER_CARD_PAGE_RESULT: {
        path: '/kort/order-card-signing-result',
        searchParam: ['kontoid'] as const,
        link: 'linkOrderCardResult',
    } as PathWithSearch,
    ORDER_CARD_FORM_PIN_DECISION_PAGE: {
        path: '/kort/bestill/behold-pin',
        searchParam: ['kontonummer', 'kontoid'] as const,
        link: 'linkOrderCardPinDecision',
    } as PathWithSearch,
    ORDER_CARD_FORM_SELECT_PIN_PAGE: {
        path: '/kort/bestill/velg-pin',
        searchParam: ['kontonummer', 'kontoid'] as const,
        link: 'linkOrderCardSelectPin',
    } as PathWithSearch,
    ORDER_CARD_FORM_SUMMARY_PAGE: {
        path: '/kort/bestill/oppsummering',
        searchParam: ['kontonummer', 'kontoid'] as const,
        link: 'linkOrderCardSummary',
    } as PathWithSearch,
    ORDER_CARD_FORM_SUMMARY_CONFIRM_PAGE: { path: '/kort/bestill/bekreft', link: 'linkOrderCardConfirm' } as Path,
    CLOSE_CREDIT_ACCOUNT_TRANSFER_FORM_PAGE: {
        path: '/kort/close-credit-account-transfer-form',
        link: 'linkCloseCreditAccountTransfer',
        searchParam: ['avslutt', 'cid', 'ctype'],
    } as PathWithSearch,
    CARD_CREDIT_LIMIT_PAGE: {
        path: '/kort/detaljer/kredittgrense',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkCreditLimit',
    } as PathWithSearch,
    CARD_CONTACT_INFO_PAGE: { path: '/kort/kontaktinformasjon', link: 'linkContactInfo' } as Path,
    CARD_UPDATE_CONTACT_INFO_PAGE: { path: '/kort/kontaktinformasjon/endre', link: 'linkContactInfoEdit' } as Path,
    GOOGLE_WALLET: {
        path: '/kort/detaljer/google-wallet',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkGoogleWallet',
    } as PathWithSearch,
    ADD_TO_GOOGLE_WALLET: {
        path: '/kort/detaljer/add-to-google-wallet',
        searchParam: ['cid', 'ctype'] as const,
        link: 'linkGooglePay',
    } as PathWithSearch,
    CLICK_TO_PAY: {
        path: '/click2pay',
        searchParam: ['email', 'ecid'] as const,
        link: 'linkClick2Pay',
    } as PathWithSearch,
    COMING_SOON_PATH: { path: '/commingsoon', link: 'linkComingSoon' } as Path,
} as const;

export type PATHS = keyof typeof paths;

type KeysOfPath<T extends PATHS> = (typeof paths)[T];

export type SearchKeys<T extends PATHS> =
    KeysOfPath<T> extends { searchParam: readonly string[] }
        ? { [key in KeysOfPath<T>['searchParam'][number]]: string | undefined } & {
              [key: string]: string | undefined;
          }
        : undefined;

const cleanUpNotValidParams = (params: URLSearchParams) => {
    const keysForDel: string[] = [];
    params.forEach((value, key) => {
        if (value === '' || value === 'undefined' || value === 'null') {
            keysForDel.push(key);
        }
    });

    keysForDel.forEach((key) => {
        params.delete(key);
    });
};

export type SearchParamsType = Record<string, string>;

type NavigationGenerateFunction = <T extends PATHS, S = SearchKeys<T>>(
    path: T,
) => (...args: S extends undefined ? [] : [S]) => string;

// NOTE: path is for navigation within react-router
//

// for navigation using links
export const generateV3Link: NavigationGenerateFunction =
    (path) =>
    (...args) => {
        const params = args[0] && new URLSearchParams(args[0]);

        if (typeof params !== 'undefined') cleanUpNotValidParams(params);

        return `#${paths[path].path}${params ? `?${params.toString()}` : ''}`;
    };

type AppNavigateConfig =
    | {
          keepParams: string[] | boolean;
          location: Location;
      }
    | Record<string, never>;

type PathGenerateFunction = <T extends PATHS, S = SearchKeys<T>>(
    path: T,
    config?: AppNavigateConfig,
) => (...args: S extends undefined ? [] : [S]) => string;

export type PathNavigateFunction = <T extends PATHS>(
    path: T,
    config?: AppNavigateConfig,
) => (...args: SearchKeys<T> extends undefined ? [] : [SearchKeys<T>]) => void;

const getKeepParams = (keepParams: string[] | boolean, location: Location): SearchParamsType => {
    const searchParams = new URLSearchParams(location.search);
    if (typeof keepParams === 'boolean') {
        return Object.fromEntries(searchParams.entries());
    }
    const keepSearchParams = keepParams.reduce((acc, cur) => ({ ...acc, [cur]: searchParams.get(cur) }), {});
    return keepSearchParams;
};

const getSearchParams = <T extends PATHS, S = SearchKeys<T>>(
    searchParams: S | undefined,
    config: AppNavigateConfig,
): SearchParamsType => {
    const otherParams = config.keepParams ? getKeepParams(config.keepParams, config.location) : {};
    return {
        ...otherParams,
        ...(searchParams || {}),
    };
};

export const generateFullPath: PathGenerateFunction =
    (path, config = {}) =>
    (...args) => {
        const params = args[0];
        const searchParams = getSearchParams(params, config);

        return `${paths[path].path}${searchParams ? `?${createSearchParams(searchParams)}` : ''}`;
    };
